import {
    Button,
    Modal,
    Upload,
    Space, Alert,
    Spin,
} from 'antd';
import cn from 'classnames';
import React, {FC, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';

import {IEditFormArgs} from 'components/documents/file-modal/documents-file-upload-modal-utils';
import {EditableTable, TEditableColumn} from 'components/editable-table';
import {TableAction} from 'components/table/action';
import {IMetaActionInfo} from 'entities/metadata';
import {TEntity} from 'modules/data';
import {ReactComponent as CloseIcon} from 'shared/assets/close.svg';
import {ReactComponent as ErrorAlert} from 'shared/assets/error-alert.svg';
import {ReactComponent as PlusCircleOutlined} from 'shared/assets/plus-outlined.svg';
import {
    IDocumentUploadedSignature,
    IDocumentUploadFile,
    resetFileList,
} from 'store/slices/documents-uploading-slice';
import {TModalComponentInterface} from 'store/slices/modals-slice';

import './file-upload-modal.less';

export interface IFileUploadModalProps extends TModalComponentInterface {
    onUpload: (file: File, files: File[]) => void;
    onClear?: () => void;
    handleEdit: (row: TEntity, args?: IEditFormArgs) => void;
    columns?: TEditableColumn[];
    fileList: IDocumentUploadFile[] | IDocumentUploadedSignature[];
    onSubmit: () => void;
    onClose: () => void;
    modalTitle?: string;
    clearText?: string;
    closeText?: string;
    submitText?: string;
    buttonText?: string;
    errorMsg?: string | null;
    rowKey?: string;
    columnsFiltered?: boolean;
    accept?: string;
    actions?: IMetaActionInfo[];
    isPageJumperEnabled?: boolean;
    isPageSizeChangerEnabled?: boolean;
    isSubmitButtonDisabled?: boolean;
    isLoading?: boolean;
}

export const FileUploadModal: FC<IFileUploadModalProps> = ({
    onClose,
    onSubmit,
    onUpload,
    onClear,
    handleEdit,
    columns,
    fileList,
    modalTitle = 'Загрузка файлов',
    clearText = 'Очистить таблицу',
    closeText = 'Отменить',
    submitText = 'Загрузить добавленные документы',
    buttonText = 'Добавить файл',
    errorMsg,
    rowKey,
    columnsFiltered,
    actions,
    accept,
    isPageJumperEnabled,
    isPageSizeChangerEnabled,
    isSubmitButtonDisabled,
    isLoading = false,
}: IFileUploadModalProps) => {
    const uploadButtonRef = useRef<HTMLButtonElement>(null);
    const {entityName} = useParams<{ entityName: string }>();
    const dispatch = useDispatch();

    useEffect(() => {
        const shouldClick = fileList.length === 0;

        if (shouldClick) uploadButtonRef.current?.click();
    }, []);

    const handleUpload = (file: File, files: File[]) => {
        onUpload(file, files);
        return false;
    };

    const clearAll = () => {
        dispatch(() => {
            resetFileList({entityName});
        });
        if (onClear) onClear();
    };

    return (
        <Modal
            title={modalTitle}
            className="modal-header"
            visible
            onCancel={onClose}
            width="95vw"
            maskClosable={false}
            closeIcon={<CloseIcon className="modal-close-icon with-title" />}
            footer={null}
        >
            <div className="table-container__actions">
                <Upload
                    beforeUpload={handleUpload as (file: File, fileList: File[]) => boolean}
                    showUploadList={false}
                    accept={accept}
                    multiple
                    disabled={isLoading}
                >
                    <Button
                        icon={<PlusCircleOutlined />}
                        ref={uploadButtonRef}
                        type="primary"
                        className="file-upload-table__actions__upload-button"
                    >
                        {buttonText}
                    </Button>
                </Upload>
                {actions?.map(action => (
                    <TableAction
                        meta={action}
                        key={action.actionTitle}
                        entityName={entityName}
                    />
                ))}
                {errorMsg && (
                    <Alert
                        className="file-upload-table__alert"
                        message={errorMsg}
                        type="error"
                        icon={<ErrorAlert />}
                        showIcon
                    />
                )}
            </div>
            <Spin
                spinning={isLoading}
            >
                <div className="file-upload-table">
                    {columns && columnsFiltered
                && (
                    <EditableTable
                        entityName={entityName}
                        scroll={{
                            x: 'max-content',
                        }}
                        handleSave={handleEdit}
                        columns={columns}
                        dataSource={fileList}
                        rowKey={rowKey}
                        isPageJumperEnabled={isPageJumperEnabled}
                        isPageSizeChangerEnabled={isPageSizeChangerEnabled}
                        isIndexRelative
                    />
                )}
                    <Space
                        className={cn(
                            'file-upload-table__actions',
                            (!fileList.length || !columns) && 'relative',
                        )}
                        size="small"
                    >
                        <Button
                            key={submitText}
                            type="primary"
                            onClick={onSubmit}
                            disabled={isSubmitButtonDisabled}
                        >
                            {submitText}
                        </Button>
                        {onClear && (
                            <Button
                                key={clearText}
                                onClick={clearAll}
                                type="default"
                                className="file-upload-table__actions__clear-button"
                            >
                                {clearText}
                            </Button>
                        )}
                        <Button
                            key={closeText}
                            onClick={onClose}
                            type="default"
                        >
                            {closeText}
                        </Button>
                    </Space>
                </div>
            </Spin>
        </Modal>
    );
};
